import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, Formik } from "formik";
import React from "react";
import {
  Button,
  Card,
  Form,
  FormGroup,
  Modal,
  Overlay,
  Popover,
  Row,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { isNullOrUndefined } from "util";
import * as Yup from "yup";
import {
  createTransactions,
  createTransactionsTagLevel,
} from "../../../../docuclipper/api";
import { ReduxState } from "../../../../redux";
import { createAlert } from "../../../../redux/reducers/Alerts";
import { fetchTotals } from "../../../../redux/reducers/Reconciler";
import {
  addAddedManually,
  fetchMfs,
  setTls,
  toggleLoading,
} from "../../../../redux/reducers/TransactionManager";
import {
  formikInput,
  formikReactSelectInput,
  formikSelectInput,
} from "../../../../utils/utils";
import AccountSelector from "./AccountSelector";
import { groupBy, uniq } from "lodash";
import AlertBanner from "../../../Docuclipper/AlertBanner/AlertBanner";
import { getDocumentName } from "../../Analyze/AnalyzeTransactionsTable/utils";
import { useChunkData } from "../../Analyze/AnalyzeTransactionsTable/hooks";

// Tiny helper that lives inside the Formik render-prop. Whenever the target
// option (current chunk built from fresh useChunkData() references) differs
// from what Formik has stored, push the new reference in via setFieldValue —
// but only if the user hasn't already picked something. This is the reliable
// way to preselect a react-select whose options object identities change every
// render, since `enableReinitialize` and reference-based `options.find` don't
// cooperate well in that case.
function ChunkPreselectSync({
  currentValue,
  targetOption,
  setFieldValue,
}: {
  currentValue: any;
  targetOption: any;
  setFieldValue: (name: string, value: any) => void;
}) {
  React.useEffect(() => {
    if (!targetOption) return;
    // If the user has already selected something different (composite key),
    // leave their choice alone.
    const userSelectedSomethingElse =
      currentValue &&
      (currentValue.accountName !== targetOption.accountName ||
        currentValue.documentId !== targetOption.documentId ||
        currentValue.chunk !== targetOption.chunk);
    if (userSelectedSomethingElse) return;
    // If the references already match (Formik already has the freshest
    // chunkOptions[i].value reference), no-op.
    if (currentValue === targetOption) return;
    setFieldValue("chunk", targetOption);
  }, [targetOption, currentValue, setFieldValue]);
  return null;
}

export function Transaction2({ onTransactionAdded }) {
  const dispatch = useDispatch();

  const {
    selectedDocumentId,
    tls,
    // selectedPage,
    mfs,
    selectedChunk,
    selectedAccount,
    // selectedAccountNickname,
  } = useSelector((state: ReduxState) => state.TransactionManager);

  const { documents } = tls;

  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );

  const addTransactionHelper = (
    {
      date,
      description,
      amount,
      fieldName,
      // documentId,
      // pageNumber,
      // account,
      chunk,
    },
    { resetForm, setFieldValue, setFieldTouched }
  ) => {
    dispatch(toggleLoading());

    // console.log({ chunk });
    const { accountName, documentId, chunk: chunk2 } = chunk;
    //TODO extract account+chunk from "chunk" +
    const pageNumber = 1;
    const extraArgs = {
      date,
      description,
      amount,
      fieldName,
      pageNumber,
      account: accountName,
      accountNickname: accountName,
      chunk: chunk2,
    };
    const fn =
      l1 && l2
        ? createTransactionsTagLevel(l1, l2, documentId, [extraArgs])
        : job && job.id
        ? createTransactions(job.id as any, documentId, [extraArgs])
        : Promise.resolve({ transactions: [] });

    fn.then(({ transactions }) => {
      dispatch(toggleLoading());
      dispatch(setTls({ transactions, resetPages: false }));
      dispatch(
        createAlert({
          message: "Transaction created successfully",
          timeout: 0,
          type: "success",
        })
      );
      dispatch(fetchTotals());
      dispatch(addAddedManually());
      dispatch(fetchMfs());
      if (onTransactionAdded) {
        onTransactionAdded();
      }
    }).catch((err) => {
      dispatch(toggleLoading());
      dispatch(
        createAlert({
          message: "Error creating transaction. Please try again.",
          timeout: 0,
          type: "error",
        })
      );
    });

    // resetForm();
    setFieldValue("date", "");
    setFieldValue("description", "");
    setFieldValue("amount", "");
    setFieldTouched("date", false);
    setFieldTouched("description", false);
    setFieldTouched("amount", false);
  };
  // const { accounts, selectedAccount } = useSelector(
  //   (state: ReduxState) => state.TransactionManager
  // );

  // TODO get chunks representation  from newsummarytable

  const chunkData = useChunkData();

  // Build the chunk options once, then use the same array for both the
  // dropdown and the preselected default. react-select compares selected vs
  // option by reference, so we need the same object instance in both places
  // for the preselection to "stick" visually.
  const chunkOptions = React.useMemo(
    () =>
      chunkData.map((chunk) => ({
        value: chunk,
        label: `${chunk.accountName} from ${chunk.startDate} to ${chunk.endDate} [${chunk.documentName} ]`,
      })),
    [chunkData],
  );

  // Preselect the Account/Period the user is currently viewing — they almost
  // always want to add the missing transaction into the period they're staring
  // at. Date defaults to today for the same reason: nine times out of ten the
  // user is logging something they just noticed in this period.
  const matchingChunkOption = React.useMemo(() => {
    if (!selectedAccount && !selectedChunk && !selectedDocumentId) return null;
    const match = chunkOptions.find(
      (opt) =>
        opt.value.accountName === selectedAccount &&
        opt.value.chunk === selectedChunk &&
        String(opt.value.documentId) === String(selectedDocumentId),
    );
    return match || null;
  }, [chunkOptions, selectedAccount, selectedChunk, selectedDocumentId]);

  const today = React.useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        date: today,
        description: "",
        amount: "",
        fieldName: tls.fieldNames.length > 0 ? tls.fieldNames[0] : "",
        // account: accounts.length > 0 ? accounts[0] : null,
        // formikReactSelectInput stores ONLY option.value in Formik state
        // (not the wrapper {value,label}), so we set the inner chunk object
        // here. The dropdown re-derives the visible label via
        // options.find(opt => opt.value === field.value).
        chunk: matchingChunkOption ? matchingChunkOption.value : null,
        // documentId: documents.length > 0 ? documents[0].id : null,
        // pageNumber: 1,
      }}
      validationSchema={Yup.object().shape({
        date: Yup.string()
          .trim()
          .matches(
            /^([0-9]{4})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/,
            "Date should be in YYYY-MM-DD format with valid month (01-12) and day (01-31)"
          )
          .required(),
        description: Yup.string().required(),
        amount: Yup.number()
          .typeError("Enter a positive or negative number")
          .required()
          .test("not-zero", "Amount cannot be 0", (value) => value !== 0),
        fieldName: Yup.string().required(),
        // account: Yup.string().required(),
        chunk: Yup.object().nullable().required("Account/Period is required"),
        // documentId: Yup.string().required(),
        // pageNumber: Yup.number().required(),
      })}
      onSubmit={addTransactionHelper}
    >
      {({ submitForm, values, setFieldValue }) => {
        return (
          <>
            {/*
              Re-sync the chunk field whenever chunkOptions or the current
              selection changes. enableReinitialize alone isn't enough because
              useChunkData returns fresh object references every render — so
              the value stored in Formik state goes stale by the time the
              dropdown's options.find runs (matches by reference, not by id).
              This effect keeps Formik's chunk pointing to a current
              chunkOptions[i].value reference while the user hasn't touched
              the field.
            */}
            <ChunkPreselectSync
              currentValue={values.chunk}
              targetOption={matchingChunkOption?.value || null}
              setFieldValue={setFieldValue}
            />
            {tls.fieldNames.length > 1 && (
              <FormGroup className="m-2">
                <strong>
                  <Form.Label>Field Name</Form.Label>
                </strong>
                <Field
                  name="fieldName"
                  as="select"
                  className=""
                  component={formikSelectInput}
                >
                  {tls.fieldNames.map((x) => (
                    <option value={x}>{x}</option>
                  ))}
                </Field>
              </FormGroup>
            )}

            <FormGroup className="m-2">
              <strong>
                <Form.Label>Account/Period</Form.Label>
              </strong>
              <Field
                name="chunk"
                component={formikReactSelectInput}
                options={chunkOptions}
              ></Field>
            </FormGroup>
            <FormGroup className="m-2">
              <strong>
                <Form.Label>Date</Form.Label>
              </strong>
              <Field name="date" component={formikInput} placeholder="" />
            </FormGroup>
            <FormGroup className="m-2">
              <strong>
                <Form.Label>Description</Form.Label>
              </strong>
              <Field
                name="description"
                component={formikInput}
                placeholder=""
              />
            </FormGroup>
            <FormGroup className="m-2">
              <strong>
                <Form.Label>Amount</Form.Label>
              </strong>
              <Field name="amount" component={formikInput} placeholder="" />
            </FormGroup>
            <Row className="justify-content-end">
              <div>
                <Button
                  variant="primary"
                  className="m-2"
                  onClick={() => {
                    submitForm();
                  }}
                >
                  <FontAwesomeIcon icon="plus" />
                  <span className="ml-1">Add missing transaction</span>
                </Button>
              </div>
            </Row>
          </>
        );
      }}
    </Formik>
  );
}
